import { useQuery } from '@tanstack/react-query';

import type { ApiSuccess, PolicyEndorsementQueueStage } from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

type QueueCountPayload = { count: number };
type QueueScope = 'admin' | 'mine';

/**
 * Both queue endpoints derive stages from status and SGA reconciliation. The
 * open set includes applied work pending manual SGA confirmation.
 */
const OPEN_QUEUE_STAGES = [
  'in_progress',
  'pending_sga_application',
  'needs_attention',
] as const satisfies readonly PolicyEndorsementQueueStage[];

/**
 * The counter needs one paginated request because only the total is rendered.
 */
function usePolicyEndorsementQueueCount(
  scope: QueueScope,
  endpoint: string,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true;
  const params = { queueStages: OPEN_QUEUE_STAGES.join(','), page: 1, limit: 1 };
  const query = useQuery({
    queryKey:
      scope === 'admin'
        ? queryKeys.policyEndorsements.adminList(params)
        : queryKeys.policyEndorsements.mineList(params),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<QueueCountPayload>>(endpoint, { params });
      return response.data.data.count;
    },
    enabled,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  return {
    data: query.data,
    isError: query.isError,
    isFetching: query.isFetching,
  };
}

export function useEndorsementQueueCount(options?: { enabled?: boolean }) {
  return usePolicyEndorsementQueueCount('admin', '/admin/policy-endorsements', options);
}

export function useMyEndorsementQueueCount(options?: { enabled?: boolean }) {
  return usePolicyEndorsementQueueCount('mine', '/policy-endorsements/mine', options);
}
